import React, { useMemo, useState } from "react";
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Badge, Box, Checkbox, Chip, CircularProgress, FormControl,
  IconButton, InputLabel, Link, MenuItem, Paper, Select, Snackbar, Stack, TextField, ToggleButton, ToggleButtonGroup,
  Tooltip, Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import AnalyticsPageShell, { CARD_SX } from "./AnalyticsPageShell";
import { TaskDialog, useRoadmapProgress } from "./RoadmapTodo";
import { CHANNELS, PRESENCE, flattenForumActions, flattenPlaces, forumTaskId } from "./marketingForumsGroupsData";

const PURPLE = "#7c3aed";
const DONE_GREEN = "#047857";

// ---------- pure helpers (exported for tests) ----------

const PRESENT = (p) => p.us.presence !== "none";

export function filterChannels(channels, { channel = "all", presence = "all", status = "all", search = "" }, states = {}) {
  const q = search.trim().toLowerCase();
  return channels
    .filter((c) => channel === "all" || c.id === channel)
    .map((c) => {
      const places = c.places
        .filter((p) => presence === "all" || (presence === "present" ? PRESENT(p) : !PRESENT(p)))
        .filter((p) => !q || [c.label, p.name, p.type, p.leader.name, p.gap, ...p.actions.map((x) => x.title)].join(" ").toLowerCase().includes(q))
        .map((p) => ({
          ...p,
          visibleActions: p.actions.filter((act) => {
            const done = !!states[forumTaskId(c.id, p.id, act.id)]?.completed;
            return status === "all" || (status === "open" ? !done : done);
          }),
        }))
        .filter((p) => status === "all" || p.visibleActions.length);
      return { ...c, places };
    })
    .filter((c) => c.places.length);
}

export function presenceSummary(channels = CHANNELS) {
  const places = flattenPlaces(channels);
  const present = places.filter(PRESENT).length;
  return { channels: channels.length, places: places.length, present, absent: places.length - present };
}

function ActionRow({ task, state, disabled, busy, onToggle, onComments }) {
  const done = !!state?.completed;
  const count = state?.comment_count || 0;
  return (
    <Box data-testid={`forum-task-${task.id}`} sx={{ display: "flex", gap: 1, py: 0.75, borderTop: "1px dashed #eef0f4", "&:first-of-type": { borderTop: 0 } }}>
      <Checkbox
        size="small"
        checked={done}
        disabled={disabled || busy}
        onChange={(e) => onToggle(task, e.target.checked)}
        icon={<CheckBoxOutlineBlankIcon fontSize="small" sx={{ color: PURPLE }} />}
        checkedIcon={<CheckBoxIcon fontSize="small" sx={{ color: DONE_GREEN }} />}
        inputProps={{ "aria-label": `Mark done: ${task.title}` }}
        sx={{ p: 0.5, alignSelf: "flex-start" }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, textDecoration: done ? "line-through" : "none", color: done ? "text.secondary" : "text.primary" }}>
          {task.title}
          {task.effort && <Chip size="small" variant="outlined" label={`~${task.effort}`} sx={{ ml: 1, height: 20, fontSize: 11 }} />}
        </Typography>
        {task.detail && <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>{task.detail}</Typography>}
        {done && (
          <Typography variant="caption" sx={{ display: "block", color: DONE_GREEN, fontWeight: 700 }} data-testid={`forum-done-${task.id}`}>
            Done{state.completed_by?.name ? ` by ${state.completed_by.name}` : ""}
          </Typography>
        )}
      </Box>
      <Tooltip title="Comments and files">
        <span>
          <IconButton size="small" onClick={() => onComments(task)} disabled={disabled} aria-label={`Comments (${count}): ${task.title}`}>
            <Badge badgeContent={count} color="secondary" max={99}><ChatBubbleOutlineIcon fontSize="small" /></Badge>
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}

function StatChip({ label, value, testId, color = PURPLE }) {
  return <Chip data-testid={testId} label={`${label}: ${value}`} sx={{ fontWeight: 700, bgcolor: `${color}14`, color }} />;
}

export default function MarketingForumsGroups() {
  const progress = useRoadmapProgress();
  const [filters, setFilters] = useState({ channel: "all", presence: "all", status: "all", search: "" });
  const [dialog, setDialog] = useState({ task: null, mode: "comments" });
  const [busyKey, setBusyKey] = useState(null);
  const [toast, setToast] = useState(null);

  const allActions = useMemo(() => flattenForumActions(), []);
  const summary = useMemo(() => presenceSummary(), []);
  const disabled = progress.loading || !!progress.error;
  const openCount = allActions.filter((t) => !progress.tasks[t.id]?.completed).length;
  const shown = filterChannels(CHANNELS, filters, progress.tasks);
  const set = (patch) => setFilters((f) => ({ ...f, ...patch }));

  const onToggle = async (task, completed) => {
    setBusyKey(task.id);
    try {
      await progress.toggle(task.id, completed);
      if (completed) setDialog({ task: { ...task, action: task.title }, mode: "done" });
      else setToast({ severity: "info", text: "Action reopened." });
    } catch (e) {
      setToast({ severity: "error", text: e?.response?.data?.message || "Could not update the action." });
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <AnalyticsPageShell
      title="Forums & groups"
      subtitle="Where 11+ parents talk, by channel: how we are doing, how the leader is doing, and the actions to become the lead voice."
    >
      <Alert severity="info" sx={{ mb: 2 }} data-testid="forum-estimate-alert">
        Sizes and presence are research estimates from the last manual check, not live counts. Where a size is unknown, count it manually and note it in a comment. Private groups are listed by name only — search for them inside the platform.
      </Alert>

      <Paper sx={{ ...CARD_SX, p: 2, mb: 2 }}>
        <Stack direction="row" flexWrap="wrap" sx={{ gap: 1 }}>
          <StatChip label="Channels" value={summary.channels} testId="forum-stat-channels" />
          <StatChip label="We have presence" value={`${summary.present}/${summary.places}`} testId="forum-stat-present" color={DONE_GREEN} />
          <StatChip label="Absent" value={`${summary.absent}/${summary.places}`} testId="forum-stat-absent" color="#b91c1c" />
          <StatChip label="Open actions" value={`${openCount}/${allActions.length}`} testId="forum-stat-open" />
          {progress.loading && <CircularProgress size={16} sx={{ alignSelf: "center" }} />}
        </Stack>
        {progress.error && <Alert severity="warning" sx={{ mt: 1 }}>{progress.error}</Alert>}
      </Paper>

      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }} sx={{ mb: 1.5 }} flexWrap="wrap">
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="forum-channel-label">Channel</InputLabel>
          <Select labelId="forum-channel-label" label="Channel" value={filters.channel} onChange={(e) => set({ channel: e.target.value })} inputProps={{ "data-testid": "forum-channel-select" }}>
            <MenuItem value="all">All channels</MenuItem>
            {CHANNELS.map((c) => <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="forum-presence-label">Presence</InputLabel>
          <Select labelId="forum-presence-label" label="Presence" value={filters.presence} onChange={(e) => set({ presence: e.target.value })} inputProps={{ "data-testid": "forum-presence-select" }}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="present">We have presence</MenuItem>
            <MenuItem value="absent">Absent</MenuItem>
          </Select>
        </FormControl>
        <ToggleButtonGroup size="small" exclusive value={filters.status} onChange={(_, v) => v && set({ status: v })} aria-label="Status filter">
          <ToggleButton value="all" sx={{ textTransform: "none", px: 1.5 }}>All</ToggleButton>
          <ToggleButton value="open" sx={{ textTransform: "none", px: 1.5 }}>Open</ToggleButton>
          <ToggleButton value="done" sx={{ textTransform: "none", px: 1.5 }}>Done</ToggleButton>
        </ToggleButtonGroup>
        <TextField size="small" label="Search" value={filters.search} onChange={(e) => set({ search: e.target.value })} inputProps={{ "data-testid": "forum-search" }} />
        <Typography variant="caption" color="text.secondary">Ticks and comments are shared with every admin.</Typography>
      </Stack>

      {!shown.length && <Typography variant="body2" color="text.secondary" data-testid="forum-empty">No places match the filters.</Typography>}

      {shown.map((c) => {
        const all = allActions.filter((t) => t.channelId === c.id);
        const done = all.filter((t) => progress.tasks[t.id]?.completed).length;
        return (
          <Accordion key={c.id} disableGutters sx={{ ...CARD_SX, mb: 1, "&:before": { display: "none" } }} data-testid={`forum-channel-${c.id}`}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{ gap: 1, flex: 1 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: c.color }} />
                <Typography sx={{ fontWeight: 700 }}>{c.label}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>{c.summary}</Typography>
                <Chip size="small" data-testid={`forum-progress-${c.id}`} label={`${done}/${all.length} done`}
                  sx={{ fontWeight: 700, bgcolor: done === all.length ? "#ecfdf5" : "#f5f3ff", color: done === all.length ? DONE_GREEN : PURPLE }} />
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              {c.places.map((p) => {
                const pres = PRESENCE[p.us.presence] || PRESENCE.none;
                return (
                  <Box key={p.id} data-testid={`forum-place-${c.id}-${p.id}`} sx={{ border: "1px solid #eef0f4", borderRadius: 1.5, p: 1.5, mb: 1.25 }}>
                    <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{ gap: 1, mb: 0.5 }}>
                      <Typography sx={{ fontWeight: 700 }}>
                        {p.url ? <Link href={p.url} target="_blank" rel="noopener noreferrer">{p.name}</Link> : p.name}
                      </Typography>
                      <Chip size="small" variant="outlined" label={p.type} sx={{ height: 20, fontSize: 11 }} />
                      <Chip size="small" label={pres.label} data-testid={`forum-presence-${c.id}-${p.id}`} sx={{ height: 20, fontSize: 11, fontWeight: 700, color: "#fff", bgcolor: pres.color }} />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>Size: {p.sizeNote}</Typography>
                    <Typography variant="body2"><strong>Leader:</strong> {p.leader.name} — {p.leader.note}</Typography>
                    <Typography variant="body2"><strong>Us:</strong> {p.us.note}{p.us.metric && p.us.metric !== "—" ? ` (${p.us.metric})` : ""}</Typography>
                    <Typography variant="body2" sx={{ mb: 0.75 }}><strong>Gap:</strong> {p.gap}</Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: PURPLE }}>Become-the-lead actions</Typography>
                    {p.visibleActions.length ? p.visibleActions.map((act) => {
                      const task = { ...act, id: forumTaskId(c.id, p.id, act.id) };
                      return (
                        <ActionRow
                          key={task.id}
                          task={task}
                          state={progress.tasks[task.id]}
                          disabled={disabled}
                          busy={busyKey === task.id}
                          onToggle={onToggle}
                          onComments={(t) => setDialog({ task: { ...t, action: t.title }, mode: "comments" })}
                        />
                      );
                    }) : <Typography variant="caption" color="text.secondary">No actions match the status filter.</Typography>}
                  </Box>
                );
              })}
            </AccordionDetails>
          </Accordion>
        );
      })}

      <TaskDialog
        task={dialog.task}
        mode={dialog.mode}
        comments={dialog.task ? progress.comments[dialog.task.id] || [] : []}
        onClose={() => setDialog({ task: null, mode: "comments" })}
        onSubmit={async (task, body, files) => { await progress.addComment(task.id, body, files); setToast({ severity: "success", text: "Comment added." }); }}
        onDelete={async (comment) => {
          try { await progress.deleteComment(comment); setToast({ severity: "success", text: "Comment deleted." }); } catch (e) { setToast({ severity: "error", text: e?.response?.data?.message || "Could not delete the comment." }); }
        }}
        onError={(text) => setToast({ severity: "error", text })}
      />
      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        {toast ? <Alert onClose={() => setToast(null)} severity={toast.severity} variant="filled" sx={{ width: "100%" }}>{toast.text}</Alert> : <span />}
      </Snackbar>
    </AnalyticsPageShell>
  );
}
